import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC, PropsWithChildren, useEffect, useMemo } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { Loader } from 'src/components/Loader';
import { RouterInput, trpc } from 'src/trpc';
import { ErrorMessage } from '@hookform/error-message';
import { TruckAddOn, TruckZ, truckAddOns, truckSizes, truckStatuses, truckZ } from '@libs/schemas';
import { z } from 'zod';
import MultipleSelector from '@/components/ui/multi-select';
type FormState = {
  isValid: boolean;
};

interface Props extends PropsWithChildren {
  truck?: RouterInput['admin']['truck']['upsertTruck'];
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  onStateChange?: (state: FormState) => void;
}

export const TruckDetailsForm: FC<Props> = ({
  truck,
  onSuccess,
  onError,
  onStateChange,
  children,
}) => {
  const utils = trpc.useUtils();
  const { mutate, isPending } = trpc.admin.truck.upsertTruck.useMutation({
    onSuccess: () => {
      onSuccess?.();
      utils.admin.truck.getAllTrucks.invalidate();
    },

    onError: (error) => {
      onError?.(new Error(error.message || 'An unknown error occurred'));
    },
  });

  // Normalize truck data:
  // 1. Convert underscores to hyphens for size field (database may have "roll_off_truck" but schema expects "roll-off-truck")
  // 2. Convert addOns from string to array if needed (schema expects array, but database may have string)
  const normalizedTruck = useMemo(() => {
    if (!truck) return undefined;

    let normalizedAddOns: TruckZ['addOns'];
    if (truck.addOns === undefined || truck.addOns === null) {
      normalizedAddOns = undefined;
    } else if (typeof truck.addOns === 'string') {
      // Convert string to array
      normalizedAddOns = [truck.addOns as TruckAddOn];
    } else if (Array.isArray(truck.addOns)) {
      normalizedAddOns = truck.addOns as TruckZ['addOns'];
    } else {
      normalizedAddOns = undefined;
    }

    // Only include size if it exists and is valid, otherwise omit it to let form use defaultValues
    const normalizedSize = truck.size
      ? (truck.size.replace(/_/g, '-') as TruckZ['size'])
      : undefined;

    // Build normalized object, only including fields that exist
    const normalized: Partial<TruckZ> = {
      _id: truck._id,
      status: truck.status as TruckZ['status'],
      addOns: normalizedAddOns,
    };

    // Only include size if it's defined (required by schema)
    if (normalizedSize) {
      normalized.size = normalizedSize;
    }

    // Return undefined if size is missing (required field), so form uses defaultValues
    if (!normalizedSize) {
      return undefined;
    }

    return normalized as TruckZ;
  }, [truck]);

  const form = useForm({
    resolver: zodResolver(truckZ),
    defaultValues: {
      status: 'available',
      size: 'flat-bed-ford', // Default size required by schema
    } as TruckZ,
    values: normalizedTruck,
    mode: 'onChange',
  });

  const { isValid, errors } = useFormState({ control: form.control });

  useEffect(() => {
    onStateChange?.({ isValid });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid]);

  const onSubmit = (values: z.infer<typeof truckZ>) => {
    mutate(values);
  };

  if (isPending) {
    return <Loader title={truck?._id ? 'Updating truck details' : 'Creating truck'} />;
  }
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-6">
          <div className="flex flex-row gap-4">
            <FormField
              control={form.control}
              name="size"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel className="label">Size</FormLabel>
                  <FormControl>
                    <Select
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(value);
                        form.trigger();
                      }}
                    >
                      <SelectTrigger className="text-text-light">
                        <SelectValue className="text-text-light" placeholder="Select truck size" />
                      </SelectTrigger>
                      <SelectContent>
                        {truckSizes?.map((size) => (
                          <SelectItem key={size} value={size}>
                            {size
                              .split('-')
                              .join(' ')
                              .replace(/\b\w/g, (l) => l.toUpperCase())}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <ErrorMessage errors={errors.size} name="size" />
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel className="label">Status</FormLabel>
                  <FormControl>
                    <Select
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(value);
                        form.trigger();
                      }}
                    >
                      <SelectTrigger className="text-text-light">
                        <SelectValue className="text-text-light" placeholder="Truck status" />
                      </SelectTrigger>
                      <SelectContent>
                        {truckStatuses?.map((status) => (
                          <SelectItem key={status} value={status}>
                            {status.replace(/\b\w/g, (l) => l.toUpperCase())}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <ErrorMessage errors={errors.status} name="status" />
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="addOns"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel className="label">Add-ons</FormLabel>
                <FormControl>
                  <MultipleSelector
                    hidePlaceholderWhenSelected
                    badgeClassName="bg-primary text-white hover:bg-primary/80"
                    defaultOptions={truckAddOns?.map((addOn) => {
                      return {
                        label: addOn
                          .split('-')
                          .join(' ')
                          .replace(/\b\w/g, (l) => l.toUpperCase()),
                        value: addOn,
                      };
                    })}
                    value={
                      Array.isArray(field.value) && field.value.length > 0
                        ? field.value.map((v) => {
                            return {
                              label: v
                                .split('-')
                                .join(' ')
                                .replace(/\b\w/g, (l) => l.toUpperCase()),
                              value: v,
                            };
                          })
                        : []
                    }
                    onChange={(options) => {
                      field.onChange(options.map((o) => o.value));
                      form.trigger();
                    }}
                    placeholder="Select truck add-ons"
                    emptyIndicator={
                      <p className="text-center text-sm leading-10 text-text-light">
                        No add-on found.
                      </p>
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {children}
        </div>
      </form>
    </Form>
  );
};
